// import useCustomQuery from 'hooks/useCustomQuery';
import * as API from './api';
import * as DTO from './dto';

import { useMutation, useQuery, useQueryClient } from 'react-query';

interface HandleReturn {
  onError?: () => void;
  onSuccess?: <Response>(data?: Response) => void;
}

interface CustomMutation<Query, Response> {
  handleReturn?: HandleReturn;
  key: string;
  queriesToInvalidate?: string[];
  request: (params: Query) => Promise<Response>;
}

interface CustomQuery<Query, Response> {
  enabled?: boolean;
  key: string;
  params: Query;
  request: (params: Query) => Promise<Response>;
}

function useCustomQuery<Query, Response>({
  enabled = true,
  key,
  params,
  request,
}: CustomQuery<Query, Response>) {
  return useQuery({
    enabled,
    queryFn: () => request(params),
    queryKey: [key, params],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 3000,
  });
}

function useCustomMutation<Query, Response>({
  handleReturn,
  key,
  request,
  queriesToInvalidate,
}: CustomMutation<Query, Response>) {
  const queryClient = useQueryClient();
  return useMutation([key], (params: Query) => request(params), {
    onError: () => {
      if (handleReturn?.onError) handleReturn?.onError();
    },
    onSuccess: data => {
      if (queriesToInvalidate) {
        queriesToInvalidate.map(query => {
          queryClient.invalidateQueries(query);
        });
      }
      if (handleReturn?.onSuccess) handleReturn?.onSuccess(data);
    },
  });
}

export function useGetAllBillingCenterData(params: DTO.BillingCenterQuery) {
  return useCustomQuery({
    key: 'getAllBillingCenterData',
    params,
    request: API.getBillingCenterData,
  });
}

export function useGetBillingCenterSummaryData(handleReturn) {
  return useCustomMutation({
    key: 'getBillingCenterSummaryData',
    request: API.getBillingCenterSummaryData,
    handleReturn,
  });
}

export function useGetMatrixDocuments(handleReturn?: HandleReturn) {
  return useCustomMutation({
    key: 'getMatrixDocuments',
    request: API.getMatrixDocuments,
    handleReturn,
  });
}

export function useGetBillingCenterFilters(params: { groupId: number }) {
  return useCustomQuery({
    key: 'getBillingCenterFilters',
    request: API.getFilterOptions,
    params,
  });
}

export function useGetPdfDocumentsByProposalId(handleReturn?: HandleReturn) {
  return useCustomMutation({
    key: 'getPdfDocumentsByProposalId',
    request: API.getPdfDocumentsByProposalId,
    handleReturn,
  });
}

export function useDeleteDocument(handleReturn?: HandleReturn) {
  return useCustomMutation({
    key: 'deleteDocument',
    request: API.deleteDocument,
    handleReturn,
  });
}
